import _ from 'lodash';
import {RequiredFieldsFromModels, Model} from './model-types.js';

/**
 *
 * This typically used as a lazy way of fetching something from the database and picking off only the
 * fields that the swagger model specifies in the method.
 *
 * @param object Swagger validated object from an API endpoint
 * @param models Object containing swagger models. Must be cast `as const` in order for inference to work.
 * @param modelName Name of root model from models object
 */
export function pickModelFields<
  Modals extends Record<string, Model<any>>,
  ModalName extends keyof Modals,
  O extends RequiredFieldsFromModels<Modals, ModalName>
>(object: O, models: Modals, modelName: ModalName): RequiredFieldsFromModels<Modals, ModalName> {
  const returnObj: any = {};
  _.forOwn(models[modelName].properties, (value, key) => {
    const objValue = object[key];
    if (_.isPlainObject(objValue)) {
      returnObj[key] = pickModelFields(objValue as any, models, value.$ref || value.type);
    } else if (_.isArray(objValue)) {
      if (!_.isUndefined(_.get(value, 'items.type'))) {
        // native type
        returnObj[key] = objValue;
      } else {
        returnObj[key] = _.map(objValue, (subArrValue) => {
          return pickModelFields(subArrValue, models, value.items.$ref);
        });
      }
    } else if (!_.isUndefined(objValue)) {
      returnObj[key] = objValue;
    }
  });

  return returnObj;
}

// TODO: We are not able to infer stuff because lodash paths are too wishy washy for TS currently
//  We'd have to move to an array based method.
// TODO: Revisit typing
// This is very similar to lodash's _.pick method, but also works on nested objects and allows renaming the destination field.
export function pick<O>(obj: O, fields: string[]): unknown {
  const result = {};
  for (const field of fields) {
    let [lhs, rhs] = field.split(' as ');
    _.set(result, rhs || lhs, _.get(obj, lhs));
  }
  return result;
}

// TODO: Revisit typing
// This is very similar to lodash's _.pick method, but also works on nested objects.
export function pickFields<O>(object: O, validFields: string[]): unknown {
  if (_.isEmpty(validFields)) {
    return object;
  }

  const filteredObject = {};

  validFields.forEach(function (fieldName) {
    const value = _.get(object, fieldName);
    if (value !== undefined) {
      _.set(filteredObject, fieldName, value);
    }
  });

  return filteredObject;
}

type ArrToUnion<T extends string[]> = T[number];

// TODO: Why is requiredFields... separate?
//  Why return undefined if fields is empty??
export function getProjection<
  F0 extends string,
  F extends F0[],
  R0 extends string = never,
  R extends R0[] = []
>(fields: F, requiredFields?: R): Record<ArrToUnion<F> | ArrToUnion<R>, 1> | undefined {
  if (_.isEmpty(fields)) {
    return undefined;
  }

  const projection: Record<string, 1> = {};

  for (const field of [...fields, ...(requiredFields || [])]) {
    projection[field] = 1;
  }

  return projection;
}

type PickArrOrObj<T, F> = T extends Array<infer O>
  ? F extends keyof O
    ? Array<Pick<O, F>>
    : Array<any>
  : F extends keyof T
  ? Pick<T, F>
  : any;

export function filterFields<O, F0 extends string, F extends F0[]>(
  result: O,
  fields: F
): PickArrOrObj<O, ArrToUnion<F>> {
  // TODO: This does not appear to be right either...
  //  if the fields array is empty, then {} or [{}, ...] should be returned since there
  //  was nothing to pick...
  if (_.isEmpty(fields)) {
    return result as any;
  }

  // TODO: This line doesn't do anything... Should we iterate over [...fields, 'id'] ?
  // [].concat(fields).push('id');

  if (result instanceof Array) {
    return result.map((item) => pickFields(item, fields)) as any;
  } else {
    return pickFields(result, fields) as any;
  }
}

// Recurses over own properties of an object invoking iteratee for each matching property.
//   predicate can be:
//     a truthy function with arguments: (value, key, parent, path)
//     a lodash path string (i.e. 'subObj.field' or 'subObj.subArr[1].field')
//     an array of lodash path strings
//   iteratee is invoked with arguments: (value, key, parent, path)
// This should be possible to build the types up for this, but... difficult
//  TODO: Exercise left to the reader
export function forOwnMatchesDeep<O>(
  obj: O,
  predicate:
    | string
    | string[]
    | (<P>(value: any, key: keyof P, parent: P, path: string) => boolean),
  iteratee: <P>(value: any, key: keyof P, parent: P, path: string) => void
) {
  (function recurse<O>(parent: O, parentPath?: string) {
    _.forOwn(parent, (value, key) => {
      const subKey = _.isArray(parent) ? `[${key}]` : `.${key}`;
      const path = parentPath ? `${parentPath}${subKey}` : key;

      if (
        (_.isFunction(predicate) && predicate(value, key as any, parent, path)) ||
        (_.isString(predicate) && predicate === path) ||
        (_.isArray(predicate) && _.includes(predicate, path))
      ) {
        iteratee(value, key as any, parent, path);
      }

      if (_.isObject(value)) {
        // Also seems to work with arrays!
        recurse(value, path);
      }
    });
  })(obj);
}

export const isNil = _.isNil;

export function isNilOrEmpty(obj: any): obj is void | '' {
  return _.isNil(obj) || obj === '';
}

// This finds all the names of the properties on T which do not have an associated value of undefined or null
type NoNilNames<T> = {[P in keyof T]: T[P] extends undefined | null ? never : P}[keyof T];

// This one won't type check correctly unless the empty type is the literal ''.
//  We could make it so fields with a string type are optional... that way you have to check first?
type NoNilOrEmptyNames<T> = {
  [P in keyof T]: T[P] extends undefined | null | '' ? never : P;
}[keyof T];
